import type { CashbackConfigResponse } from "@cede/types";
import { API_ENDPOINT, QueryKeys } from "@cede/utils";
import { useApi } from "@/hooks/core/useApi";
import { useQuery } from "@tanstack/react-query";

interface UseCashbackConfigOptions {
  enabled?: boolean;
}

/**
 * Hook to fetch the global cashback configuration
 * Returns cashback rate, thresholds, caps, and enabled exchanges
 * This is a public endpoint and doesn't require authentication
 */
export const useCashbackConfig = (options?: UseCashbackConfigOptions) => {
  const apiHelper = useApi();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [QueryKeys.CASHBACK_CONFIG],
    queryFn: async (): Promise<CashbackConfigResponse> => {
      return apiHelper.get(API_ENDPOINT.CASHBACK_CONFIG);
    },
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000, // 5 minutes - config doesn't change often
    retry: 2,
  });

  return {
    config: data ?? null,
    cashbackRate: data?.cashbackRate ?? null,
    unlockThresholdUsd: data?.unlockThresholdUsd ?? null,
    monthlyCapUsd: data?.monthlyCapUsd ?? null,
    isActive: data?.isActive ?? false,
    enabledExchanges: data?.enabledExchanges ?? [],
    isLoading,
    error: error?.message ?? null,
    refetch,
  };
};
